import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import FileSaver from 'file-saver';
import { MessageService } from 'primeng';
import { Message } from '../classes/Message';
import { HttpResponsesHandlersFactory } from '../handlers/http-responses-handler';
import { IFacetedInboxTask, IFacetedJobSearch } from '../interfaces/faceted-inbox-task';
import { IInboxJob } from '../interfaces/inbox-job';
import { IInboxJobFilter } from '../interfaces/inbox-job-filter';
import { EditService } from './edit.service';
import { IFacetedFilterSavedSearch } from '../interfaces/faceted-filter-saved-search';
import moment from 'moment';
import { InboxCustomerDepartmentDTO } from '../interfaces/inbox-customer-departments';
import { InboxCustomerTeamDTO } from '../interfaces/inbox-customer-team';

@Injectable({ providedIn: 'root' })
export class VmeInboxJobsService extends EditService<IInboxJob, IInboxJobFilter> {
  baseUrl = '/api/inboxjobs';
  url = this.baseUrl + '/getVmeJobs';
  entityTitle = 'VmeInboxJobs';

  constructor(
    http: HttpClient,
    handlersFactory: HttpResponsesHandlersFactory,
    private messageService: MessageService,
  ) {
    super(http, handlersFactory);
  }

  getPage(filter: IInboxJobFilter): Promise<any> {
    let params = this.constructHttpParams(filter);
    return this.handleRequest(
      this.http.get(this.url, { params: params }),
      this.handlersFactory.getForReadByFilter(),
    ).then(this.extractData);
  }

  public getAllJobs(
    showAllJobs: boolean,
    showInspectionTasks: boolean,
  ): Promise<IFacetedInboxTask[]> {
    return this.handleRequest(
      this.http.get<IFacetedInboxTask[]>(
        `${this.baseUrl}/getAllJobs/${showAllJobs}/${showInspectionTasks}`,
      ),
      this.handlersFactory.getForReadAll(),
    );
  }

  public facetedJobSearch(dto: IFacetedJobSearch): Promise<IFacetedInboxTask[]> {
    return this.handleRequest(
      this.http.get<IFacetedInboxTask[]>(`${this.baseUrl}/facetedJobSearch`, {
        params: this.constructHttpParams(dto),
      }),
      this.handlersFactory.getForReadAll(),
    );
  }

  public getSavedFacetedSearches(): Promise<IFacetedFilterSavedSearch[]> {
    return this.handleRequest(
      this.http.get<IFacetedFilterSavedSearch[]>(`${this.baseUrl}/getSavedFacetedSearches`),
      this.handlersFactory.getForReadAll(),
    );
  }

  public saveFacetedSearch(search: IFacetedFilterSavedSearch) {
    return this.http.post<any>(`${this.baseUrl}/saveFacetedSearch`, search).toPromise();
    // return this.handleRequest(
    //     this.http.post<any>(`${this.baseUrl}/saveFacetedSearch`, search),
    //     this.handlersFactory.getForUpdate("Faceted Filter Search"),
    //     "Search Filters Saved"
    // );
  }

  public getFacetedSearch(id: number): Promise<IFacetedFilterSavedSearch> {
    return this.handleRequest(
      this.http.get<IFacetedFilterSavedSearch>(`${this.baseUrl}/getFacetedSearch/${id}`),
      this.handlersFactory.getForReadAll(),
    );
  }

  public deleteFacetedSearch(id: number) {
    return this.http.delete(`${this.baseUrl}/deleteFacetedSearch/${id}`).toPromise();
  }

  public getCustomers(): Promise<any[]> {
    return this.handleRequest(
      this.http.get<any[]>(`${this.baseUrl}/getCustomers`),
      this.handlersFactory.getForReadAll(),
    );
  }

  public getSuppliers(): Promise<any[]> {
    return this.handleRequest(
      this.http.get<any[]>(`${this.baseUrl}/getSuppliers`),
      this.handlersFactory.getForReadAll(),
    );
  }

  public getCustomerDepartments(): Promise<InboxCustomerDepartmentDTO[]> {
    return this.handleRequest(
      this.http.get<any[]>(`${this.baseUrl}/getCustomerDepartments`),
      this.handlersFactory.getForReadAll(),
    );
  }

  public getInboxCustomerTeamsByDepartmentIds(
    customerDepartmentIds: number[],
  ): Promise<InboxCustomerTeamDTO[]> {
    return this.http
      .post<any>(
        `${this.baseUrl}/getInboxCustomerTeamsByDepartmentIds`,
        {
          customerDepartmentIds: customerDepartmentIds,
        },
        {},
      )
      .toPromise();
  }

  public getJobCounts(): Promise<any> {
    return this.handleRequest(
      this.http.get<any>(`${this.baseUrl}/getVmeJobCounts`),
      this.handlersFactory.getForReadAll(),
    );
  }

  public getVmeInboxUnclicked(inboxType: number): Promise<number> {
    return this.handleRequest(
      this.http.get(`${this.baseUrl}/getVmeInboxUnclicked?inboxType=${inboxType}`, {
        responseType: 'text',
      }),
      this.handlersFactory.getForReadAll(),
    ).then(parseFloat);
  }

  public getVmeInboxToCombine(): Promise<number> {
    return this.handleRequest(
      this.http.get(`${this.baseUrl}/getVmeInboxToCombine`, { responseType: 'text' }),
      this.handlersFactory.getForReadAll(),
    ).then(parseFloat);
  }

  public getJobTaggingInfo(jobNumber: string): Promise<any> {
    return this.handleRequest(
      this.http.get<any>(`${this.baseUrl}/getJobTaggingInfo?jobNumber=${jobNumber}`),
      this.handlersFactory.getForReadAll(),
    );
  }

  public combineJobs(jobIDs: number[]): Promise<any> {
    //return this.http.get(`${this.baseUrl}/combineJobs`).toPromise();
    return this.handleRequest(
      this.http.post<any>(`${this.baseUrl}/combineJobs`, { jobIds: jobIDs }),
      this.handlersFactory.getForUpdate(this.entityTitle),
      'Jobs are combined',
    );
  }

  public uncombineJobs(id: number): Promise<any> {
    return this.handleRequest(
      this.http.get<any>(`${this.baseUrl}/uncombineJobs?id=${id}`),
      this.handlersFactory.getForUpdate(this.entityTitle),
      'Job uncombined.',
    );
  }

  public markJobForCombine(id: number): Promise<any> {
    return this.handleRequest(
      this.http.get<any>(`${this.baseUrl}/markJobForCombine?id=${id}`),
      this.handlersFactory.getForUpdate(this.entityTitle),
      'Job marked for combining.',
    );
  }

  public markJobsForCombine(ids: number[]): Promise<any> {
    return this.handleRequest(
      this.http.post<any>(`${this.baseUrl}/markJobsForCombine`, ids),
      this.handlersFactory.getForUpdate(this.entityTitle),
      ids.length === 1 ? 'Job marked for combining.' : `${ids.length} jobs marked for combining.`,
    );
  }

  // this will unmark a job if it is marked, or mark it if it is unmarked (i.e. PriorityDeployment flag)
  public markPriorityDeployment(jobIds: number[]): Promise<any> {
    return this.handleRequest(
      this.http.post<any>(`${this.baseUrl}/markPriorityDeployment`, jobIds),
      this.handlersFactory.getForUpdate(this.entityTitle),
      'Job priority deployment status updated.',
    );
  }

  public removeJobFromCombine(id: number): Promise<any> {
    return this.handleRequest(
      this.http.get<any>(`${this.baseUrl}/removeJobFromCombine?id=${id}`),
      this.handlersFactory.getForUpdate(this.entityTitle),
      'Combining is cancelled.',
    );
  }

  public downloadAllTasksToCSV(tasks: IFacetedInboxTask[]) {
    //console.log("TASKS for CSV: ", tasks);

    // remove columns that are not required for export
    let csvTasks = tasks.map((x) => {
      return {
        JobNumber: x.jobNumber,
        ScheduledDate: moment(x.scheduledDate).format('ddd DD MMM YYYY HH:mm'),
        VehicleDetails: x.vehicleRegDetails,
        From: x.from,
        To: x.to,
        CollectionZone: x.pickupZoneName,
        DeliveryZone: x.deliveryZoneName,
        EarliestCollection: moment(x.earliestPickupDate).format('ddd DD MMM YYYY HH:mm'),
        EarliestDelivery: moment(x.earliestDropOffDate).format('ddd DD MMM YYYY HH:mm'),
        TaskType: x.customerTaskType,
        CustomerName: x.customerName,
        SupplierName: x.supplierName,
        SupplierPrice: x.supplierPrice,
        JobStatus: x.jobStatus,
        InboxName: x.inboxTabName,

        CreatedDate: moment(x.orderCreatedDate).format('ddd DD MMM YYYY HH:mm'),
        LatestCollection: moment(x.latestPickupDate).format('ddd DD MMM YYYY HH:mm'),
        LatestDelivery: moment(x.latestDropOffDate).format('ddd DD MMM YYYY HH:mm'),
        FromLat: x.fromLatitude,
        FromLng: x.fromLongitude,
        ToLat: x.toLatitude,
        ToLng: x.toLongitude,
        MoveDistance: x.moveDistance,
        MoveDuration: x.moveDuration,
        IsGuaranteed: x.isGuaranteed ? 'Yes' : 'No',
        IsPriceEC: x.isPriceEC ? 'Yes' : 'No',
        VehicleWeight: x.vehicleWeight,
        VehiclesNum: x.vehiclesNum,
        ChecklistCategory: x.checklistCategoryName,
        ChecklistSubCategory: x.checklistSubCategoryName,
      };
    });

    // convert all tasks to blob
    const replacer = (key, value) => (value === null ? '' : value);
    const header = Object.keys(csvTasks[0]);
    const csv = [
      header.join(','), // header row first
      ...csvTasks.map((row) =>
        header.map((fieldName) => JSON.stringify(row[fieldName], replacer)).join(','),
      ),
    ].join('\r\n');

    let filename = 'AllTasks_' + moment().format('YYYYMMMMDo_h:mm') + '.csv';
    let blob = new Blob([csv], { type: 'application/octet-stream' });
    FileSaver.saveAs(blob, filename);
  }

  public downloadToCSV(filter: IInboxJobFilter, inboxType: number) {
    let filename =
      'Inbox_' + this.getInboxName(inboxType) + '_' + moment().format('YYYYMMMMDo_h:mm') + '.csv';

    let params = this.constructHttpParams(filter);

    let report = this.http.get(`${this.baseUrl}/downloadInboxToCSV`, {
      params: params,
      responseType: 'blob',
    });
    report.subscribe(
      (x) => this.handleDownloadFile(x, filename),
      (error) => {
        this.messageService.add(Message.Error('Impossible to download the csv.'));
      },
      () => {
        this.messageService.add(
          Message.Success('Downloaded successful. Check your download folder.'),
        );
      },
    );

    return report;
  }

  public handleDownloadFile(blob, filename) {
    FileSaver.saveAs(blob, filename);
  }

  public getInboxName(inboxType?: number) {
    switch (inboxType) {
      case 1:
        return 'SupplierToBeConfirmed';
      case 2:
        return 'AwaitingStart';
      case 3:
        return 'Completed';
      case 4:
        return 'Combine';
      case 5:
        return 'AdditionalCostsRequests';
      case 6:
        return 'LiveNow';
      case 8:
        return 'Closed';
      case 9:
        return 'Cancelled';
      case 10:
        return 'AllJobs';
      case 11:
        return 'Deployment';
      case 12:
        return 'ExistingCapacity';
    }
  }

  holdJobs(jobIds: number[], releaseDate: Date): Promise<void> {
    // formatted, not sent as a Date, which would serialise to UTC and arrive an hour out in BST
    return this.http
      .post<void>(`${this.baseUrl}/jobs/hold`, {
        jobIds,
        releaseDate: moment(releaseDate).format('YYYY-MM-DD HH:mm'),
      })
      .toPromise();
  }

  releaseJobs(jobIds: number[]): Promise<void> {
    return this.http.post<void>(`${this.baseUrl}/jobs/release`, jobIds).toPromise();
  }
}
